import * as yup from 'yup';
import {
  AnySchema,
  BuiltInValidation,
  UserErrorMessages,
  UserValidation,
  ValidationObject,
  ValidationType
} from '../../../../types';
import { buildArrayValidators } from './buildArrayValidators';
import { type BooleanValdiation, buildBooleanValidators } from './buildBooleanValidators';
import { buildCustomFieldSchema } from './buildCustomFieldSchema';
import { buildDateValidators, type DateValidation } from './buildDateValidators';
import { buildNumberValidators, type NumberValidation } from './buildNumberValidators';
import { buildObjectValidators, type ObjectValidation } from './buildObjectValidators';
import { buildStringValidators, type StringValidation } from './buildStringValidators';

export function buildEntitySchema(
  {
    userValidation,
    errorMessages,
    builtin,
    subTypeSchema,
    subtype,
    type
  }: Pick<
    ValidationObject<string>,
    'userValidation' | 'builtin' | 'subTypeSchema' | 'subtype' | 'type' | 'errorMessages'
  >,
  parentSchemaDefinition?: { [key: string]: any }
): AnySchema {
  return buildYupSchema(
    type,
    { ...userValidation },
    { ...errorMessages },
    builtin,
    type === ValidationType.ARRAY && !!subtype && subTypeSchema ? subTypeSchema : undefined,
    parentSchemaDefinition
  );
}

function buildYupSchema(
  type: ValidationType,
  userValidation?: UserValidation,
  errorMessages?: UserErrorMessages,
  builtInValidation?: BuiltInValidation,
  subSchema?: yup.Schema<any, object>,
  parentSchemaDefinition?: { [key: string]: any }
) {
  let currentSchema: AnySchema;
  switch (type) {
    case 'date':
      currentSchema = buildDateValidators(
        userValidation as DateValidation,
        errorMessages,
        parentSchemaDefinition
      ) as AnySchema;
      break;
    case 'string':
      currentSchema = buildStringValidators(userValidation as StringValidation) as AnySchema;
      break;
    case 'number':
      currentSchema = buildNumberValidators(userValidation as NumberValidation) as AnySchema;
      break;
    case 'object':
      currentSchema = buildObjectValidators(
        userValidation as ObjectValidation,
        errorMessages,
        !!builtInValidation
      ) as AnySchema;
      break;
    case 'array':
      currentSchema = buildArrayValidators(userValidation ?? {}, errorMessages, subSchema, !!builtInValidation);
      break;
    case 'boolean':
      currentSchema = buildBooleanValidators(userValidation as BooleanValdiation) as AnySchema;
      break;
    case 'customField':
      currentSchema = buildCustomFieldSchema(userValidation ?? {}, errorMessages) as AnySchema;
      break;
    default:
      currentSchema = buildStringValidators(userValidation as StringValidation) as AnySchema;
      break;
  }

  if (builtInValidation && type !== 'customField') {
    currentSchema = builtInValidation(currentSchema, userValidation ?? {}, errorMessages);
  }
  return currentSchema;
}

export function isEntity(obj: Record<string, any> | ValidationObject<string>): obj is ValidationObject<string> {
  return !!(obj as ValidationObject<string>).elementType;
}
